import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, subMonths } from 'date-fns';
import { DollarSign, TrendingUp, TrendingDown, Fuel, Wrench, BarChart3, AlertCircle, CheckCircle } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { vehicleROIService } from '../../services/vehicleROI.service';
import { vehicleService } from '../../services/vehicle.service';

export const VehicleROIDashboard: React.FC = () => {
  const [vehicleId, setVehicleId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState(subMonths(new Date(), 1).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  const { data: vehicles } = useQuery({
    queryKey: ['vehicles', 'active'],
    queryFn: vehicleService.getActive,
  });

  const { data: roiReport, isLoading, error } = useQuery({
    queryKey: ['vehicleROI', vehicleId, startDate, endDate],
    queryFn: () => vehicleROIService.getVehicleROI(vehicleId!, startDate, endDate),
    enabled: !!vehicleId,
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-neutral-charcoal">Vehicle ROI Dashboard</h1>
        <p className="text-gray-600">Track vehicle profitability and return on investment</p>
      </div>

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle</label>
            <select
              value={vehicleId || ''}
              onChange={(e) => setVehicleId(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-green"
            >
              <option value="">Select Vehicle</option>
              {vehicles?.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.registrationNumber} - {v.make} {v.model}
                </option>
              ))}
            </select>
          </div>
          <Input
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <Input
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      </Card>

      {isLoading && (
        <div className="text-center py-8 text-gray-500">Loading ROI data...</div>
      )}

      {error && (
        <div className="text-center py-8 text-error">Failed to load ROI data</div>
      )}

      {!vehicleId && !isLoading && (
        <div className="text-center py-8 text-gray-500">Please select a vehicle to view ROI data</div>
      )}

      {roiReport && (
        <div className="space-y-6">
          {!roiReport.purchasingPrice && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <p className="font-semibold text-blue-900">Add purchasing price for complete ROI analysis</p>
                <p className="text-sm text-blue-700 mt-1">
                  Set the vehicle's purchasing price to unlock depreciation tracking, total cost of ownership, and break-even analysis.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-600">Total Revenue</p>
                  <DollarSign className="text-green-600" size={20} />
                </div>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(roiReport.totalRevenue)}</p>
                <p className="text-xs text-gray-500 mt-1">{roiReport.totalTrips} trips</p>
              </div>
            </Card>

            <Card>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-600">Total Expenses</p>
                  <TrendingDown className="text-red-600" size={20} />
                </div>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(roiReport.totalExpenses)}</p>
                <p className="text-xs text-gray-500 mt-1">Operating costs</p>
              </div>
            </Card>

            <Card>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-600">Net Profit</p>
                  <TrendingUp className="text-blue-600" size={20} />
                </div>
                <p className="text-2xl font-bold text-blue-600">{formatCurrency(roiReport.netProfit)}</p>
                <p className="text-xs text-gray-500 mt-1">{roiReport.profitMargin.toFixed(1)}% margin</p>
              </div>
            </Card>

            <Card>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-600">Operational ROI</p>
                  <TrendingUp className="text-primary-green" size={20} />
                </div>
                <p className="text-2xl font-bold text-primary-green">{roiReport.roi.toFixed(2)}%</p>
                <p className="text-xs text-gray-500 mt-1">Profit / Expenses</p>
              </div>
            </Card>
          </div>

          {roiReport.purchasingPrice && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-600">Total Cost of Ownership</p>
                      <BarChart3 className="text-blue-600" size={20} />
                    </div>
                    <p className="text-2xl font-bold text-blue-900">{formatCurrency(roiReport.totalCostOfOwnership)}</p>
                    <p className="text-xs text-gray-500 mt-1">Purchase + Operating</p>
                  </div>
                </Card>

                <Card>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-600">ROI w/ Depreciation</p>
                      <TrendingUp className={roiReport.roiIncludingDepreciation > 0 ? "text-green-600" : "text-red-600"} size={20} />
                    </div>
                    <p className={`text-2xl font-bold ${roiReport.roiIncludingDepreciation > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {roiReport.roiIncludingDepreciation.toFixed(2)}%
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      vs {roiReport.roi.toFixed(1)}% operational
                    </p>
                  </div>
                </Card>

                <Card>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-600">Depreciation</p>
                      <TrendingDown className="text-orange-600" size={20} />
                    </div>
                    <p className="text-2xl font-bold text-orange-600">{formatCurrency(roiReport.estimatedDepreciation)}</p>
                    <p className="text-xs text-gray-500 mt-1">20% per year</p>
                  </div>
                </Card>

                <Card>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-600">Break-Even Status</p>
                      {roiReport.totalRevenue >= roiReport.breakEvenRevenue ? (
                        <CheckCircle className="text-green-600" size={20} />
                      ) : (
                        <AlertCircle className="text-yellow-600" size={20} />
                      )}
                    </div>
                    {roiReport.totalRevenue >= roiReport.breakEvenRevenue ? (
                      <>
                        <p className="text-2xl font-bold text-green-600">
                          {((roiReport.totalRevenue / roiReport.breakEvenRevenue) * 100).toFixed(0)}%
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Exceeded by {formatCurrency(roiReport.totalRevenue - roiReport.breakEvenRevenue)}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-2xl font-bold text-yellow-600">
                          {((roiReport.totalRevenue / roiReport.breakEvenRevenue) * 100).toFixed(0)}%
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Need {formatCurrency(roiReport.breakEvenRevenue - roiReport.totalRevenue)} more
                        </p>
                      </>
                    )}
                  </div>
                </Card>
              </div>

              {roiReport.totalRevenue >= roiReport.breakEvenRevenue && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <p className="font-semibold text-green-900">Break-even achieved!</p>
                    <p className="text-sm text-green-700 mt-1">
                      This vehicle has recovered its total investment and is generating profit.
                    </p>
                  </div>
                </div>
              )}

              {roiReport.roiIncludingDepreciation < 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <p className="font-semibold text-yellow-900">Vehicle not yet profitable</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      Current deficit: {formatCurrency(Math.abs(roiReport.netProfit - roiReport.estimatedDepreciation))}. 
                      Consider reviewing expenses or increasing utilization.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {roiReport.purchasingPrice && (
              <Card title="Investment Analysis">
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-sm text-gray-600">Purchase Price</span>
                    <span className="font-semibold">{formatCurrency(roiReport.purchasingPrice)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-sm text-gray-600">Operating Expenses</span>
                    <span className="font-semibold">{formatCurrency(roiReport.totalExpenses)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-sm text-gray-600">Total Investment</span>
                    <span className="font-semibold text-blue-600">{formatCurrency(roiReport.totalCostOfOwnership)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-gray-600">Current Value</span>
                    <span className="font-semibold">
                      {formatCurrency(roiReport.purchasingPrice - roiReport.estimatedDepreciation)}
                    </span>
                  </div>
                </div>
              </Card>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card title="Expense Breakdown">
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b">
                  <div className="flex items-center gap-2">
                    <Fuel size={16} className="text-blue-600" />
                    <span className="text-sm">Fuel</span>
                  </div>
                  <span className="font-semibold">{formatCurrency(roiReport.fuelExpenses)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <div className="flex items-center gap-2">
                    <Wrench size={16} className="text-orange-600" />
                    <span className="text-sm">Maintenance</span>
                  </div>
                  <span className="font-semibold">{formatCurrency(roiReport.maintenanceExpenses)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <div className="flex items-center gap-2">
                    <Wrench size={16} className="text-red-600" />
                    <span className="text-sm">Repairs</span>
                  </div>
                  <span className="font-semibold">{formatCurrency(roiReport.repairExpenses)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <div className="flex items-center gap-2">
                    <DollarSign size={16} className="text-purple-600" />
                    <span className="text-sm">Allowances</span>
                  </div>
                  <span className="font-semibold">{formatCurrency(roiReport.totalAllowances)}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm">Other</span>
                  <span className="font-semibold">{formatCurrency(roiReport.otherExpenses)}</span>
                </div>
              </div>
            </Card>

            <Card title="Per-KM Metrics">
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-sm text-gray-600">Total Distance</span>
                  <span className="font-semibold">{roiReport.totalDistanceKm.toLocaleString()} km</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-sm text-gray-600">Revenue per KM</span>
                  <span className="font-semibold text-green-600">{formatCurrency(roiReport.revenuePerKm)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-sm text-gray-600">Expense per KM</span>
                  <span className="font-semibold text-red-600">{formatCurrency(roiReport.expensePerKm)}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-gray-600">Profit per KM</span>
                  <span className="font-semibold text-blue-600">{formatCurrency(roiReport.profitPerKm)}</span>
                </div>
              </div>
            </Card>
          </div>

          <Card title="Vehicle Information">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">Registration</p>
                <p className="font-semibold">{roiReport.registrationNumber}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Make & Model</p>
                <p className="font-semibold">{roiReport.make} {roiReport.model}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Year</p>
                <p className="font-semibold">{roiReport.year}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Report Period</p>
                <p className="font-semibold text-sm">
                  {format(new Date(roiReport.reportStartDate), 'MMM dd')} - {format(new Date(roiReport.reportEndDate), 'MMM dd, yyyy')}
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
